import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { clsx } from 'clsx';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { formatCurrencyExact } from '@/utils/trades/mtm-calculations';
import { displayCashPool, withPoolSign } from '@/utils/trades/cash-pool-display';
import { getErrorMessage } from '@/utils/get-error-message';
import { COB_FX_MAJORS } from '@/services/currency/cob-fx-service';
import { useCobEditableRate } from '@/features/trades/hooks/use-cob-editable-rate';
import type { RepoMarginCallPreview } from '@/types';
import { formatMta } from '@/utils/trades/mta-display';

const { Panel: DialogPanel, Title: DialogTitle } = Dialog;
const { Child: TransitionChild } = Transition;

interface PostRepoMarginCallDialogProps {
  show: boolean;
  onClose: () => void;
  /** The computed preview (Current Exposure / Cash Pool / Net margin call). */
  preview: RepoMarginCallPreview | null;
  /** True while the POST is in flight. */
  isLoading: boolean;
  error?: Error | null;
  /**
   * Receives the SIGNED posted amount as a 2-decimal string (sign fixed by the computed net_due),
   * the chosen payment currency (LR-493; null = pay in the settlement currency), and the manual
   * payment FX rate as a 6dp string (null = let the backend resolve the COB rate).
   */
  onConfirm: (
    postedSigned: string,
    paymentCurrency: string | null,
    paymentFxRate: string | null,
  ) => void;
  /**
   * LR-548: true while the authoritative subset preview (POST /preview/) is fetching. The prefill
   * amount / direction come from that call, so Post is blocked until it settles.
   */
  previewLoading?: boolean;
  /** LR-548: the subset preview (POST /preview/) errored — block Post rather than fall back to
   *  stale/whole-book figures for a subset call. */
  previewError?: boolean;
  /** LR-548: repo positions currently SELECTED for the call (checked). */
  selectedCount?: number;
  /**
   * LR-548: the whole eligible repo BOOK for this counterparty (review #2) — what a whole-book call
   * would cover, independent of the sidebar filter. The scope line and the subset acknowledgement are
   * measured against this, so a filter-induced subset can't read as a full call.
   */
  totalCount?: number;
  /** LR-548: eligible repo positions currently VISIBLE in the grid; < totalCount when a sidebar filter
   *  hides some. Defaults to totalCount (nothing hidden). */
  visibleCount?: number;
  /**
   * LR-548: true when the post will carry explicit `position_ids` (a strict subset). The backend
   * refuses a subset that has any un-valuable (skipped) line, so a skip must BLOCK the post here
   * (deselect / set a New Price) rather than offer the whole-book "post anyway" acknowledgement.
   */
  isSubsetCall?: boolean;
}

function directionLabel(direction: RepoMarginCallPreview['direction']): string {
  return direction === 'due_to_cp'
    ? 'Due to CP'
    : direction === 'due_to_us'
      ? 'Due to us'
      : 'Settled';
}

function netClass(value: number): string {
  return value < 0
    ? 'text-red-600'
    : value > 0
      ? 'text-green-600'
      : 'text-slate-900';
}

const SummaryRow: React.FC<{
  label: string;
  children: React.ReactNode;
  /** LR-493: optional target-ccy equivalent shown faint under the native value. */
  sub?: React.ReactNode;
  emphasize?: boolean;
}> = ({ label, children, sub, emphasize }) => (
  <div
    className={clsx(
      'flex items-center justify-between',
      emphasize && 'mt-1 border-t border-slate-200 pt-1'
    )}
  >
    <span className="text-xs text-slate-500">{label}</span>
    <span className="flex flex-col items-end">
      <span className="font-mono text-sm font-semibold tabular-nums text-slate-900">
        {children}
      </span>
      {sub && (
        <span className="font-mono text-[11px] tabular-nums text-slate-400">{sub}</span>
      )}
    </span>
  </div>
);

/**
 * LR-479: post a repo margin call with a manual override. The amount is prefilled with the
 * computed magnitude but editable — the desk posts a rounded, counterparty-agreed figure. Only the
 * magnitude is entered; the direction is fixed by sign(net_due) and can't be flipped. The live
 * "Cash pool after" / "Residual → next call" update as the amount changes. Posting 0 is allowed
 * (warned); a settled call (computed 0) disables Post — there is nothing to post.
 */
export const PostRepoMarginCallDialog: React.FC<
  PostRepoMarginCallDialogProps
> = ({
  show,
  onClose,
  preview,
  isLoading,
  error,
  onConfirm,
  previewLoading = false,
  previewError = false,
  selectedCount,
  totalCount,
  visibleCount,
  isSubsetCall = false,
}) => {
  // The call is in the GMRA agreement currency; all money figures below are in `settleCcy`.
  const settleCcy = preview?.settlement_currency || preview?.currency || 'USD';
  // Trade currencies the backend converted into the call currency, each line at its own COB rate.
  const convertedCcys = [
    ...new Set(
      (preview?.trades ?? [])
        .map((trade) => (trade.currency ?? '').toUpperCase())
        .filter((ccy) => ccy && ccy !== settleCcy.toUpperCase()),
    ),
  ].sort();
  const netDue = preview ? Number(preview.net_due) : 0;
  // LR-548: net against the SELECTED positions' share of the pool for a subset (== whole pool for a
  // whole-book call), so the shown pool-before / net_due / pool-after reconcile on screen and a subset
  // never appears to move the collateral backing the unselected positions. Falls back to the whole
  // pool if an older payload omits the field.
  const cashpoolBefore = preview
    ? Number(preview.selected_cashpool_before ?? preview.cashpool_before)
    : 0;
  const totalExposure = preview ? Number(preview.total_exposure) : 0;
  // LR-579: under Method B the exposure is no longer just "COB − contract" — it also carries the
  // unpaid accrued repo interest. Break the figure out only when there is one, so a Method A book
  // (and a Method B book with nothing accrued yet) reads exactly as before.
  const accruedComponent = preview ? Number(preview.accrued_component ?? '0') : 0;
  const showAccruedBreakdown = Math.abs(accruedComponent) >= 0.005;
  const cleanExposure = totalExposure - accruedComponent;
  const isSettled = Math.abs(netDue) < 0.005; // computed nothing to post
  const dir = netDue > 0 ? 1 : netDue < 0 ? -1 : 0;

  // Editable MAGNITUDE, prefilled with |net_due|. Re-seed only when the dialog opens for a DIFFERENT
  // book (counterparty + call currency) — NOT on every preview object identity change. A repo
  // preview refetch while the dialog is open lands a fresh object for the SAME book; re-seeding there
  // would wipe the amount / payment selection the desk has already typed (KI-7, the LR-479 "reset
  // keyed on a recomputed reference" class). lastBookRef tracks the seeded book and clears on close so
  // the next open re-seeds; the fx-rate touched-ref below already guards the rate on the same grounds.
  const [amountStr, setAmountStr] = useState('');
  // LR-479 review #1: must acknowledge excluded (un-valuable) positions before posting a partial call.
  const [ackSkipped, setAckSkipped] = useState(false);
  // LR-548: acknowledge that some selectable repo positions are DESELECTED (the call is a strict subset).
  const [ackUnselected, setAckUnselected] = useState(false);
  // LR-493: optionally pay the call in a different major than the settlement currency. '' = pay in
  // settleCcy (the default and normal case). Re-seeds on open.
  const [paymentCcy, setPaymentCcy] = useState('');
  const lastBookRef = useRef('');
  // LR-493 review L1: the amount and ack need the same "recomputed reference" guards the rate got.
  //  - amountTouchedRef: an UNTOUCHED prefill should follow a fresh net_due on a same-book refetch
  //    (KI-7 kept a TYPED amount; it must not freeze the default too), so only re-seed while untouched.
  //  - lastSkippedKeyRef: reset the excluded-positions ack when the skipped set changes, so a grown
  //    skipped[] re-blocks Post until the desk re-acknowledges (a stale ack could post an
  //    undercollection against the pool).
  const amountTouchedRef = useRef(false);
  const lastSkippedKeyRef = useRef('');
  useEffect(() => {
    if (!show) {
      lastBookRef.current = '';
      return;
    }
    if (!preview) return;
    const bookKey = `${preview.counterparty_name}|${preview.currency}`;
    const netDueDefault = Math.abs(Number(preview.net_due)).toFixed(2);
    const skippedKey = preview.skipped
      .map((s) => s.position_id)
      .sort((a, b) => a - b)
      .join(',');
    if (bookKey !== lastBookRef.current) {
      // New book (or first open) — full re-seed.
      lastBookRef.current = bookKey;
      amountTouchedRef.current = false;
      lastSkippedKeyRef.current = skippedKey;
      setAmountStr(netDueDefault);
      setAckSkipped(false);
      setAckUnselected(false);
      setPaymentCcy('');
      return;
    }
    // Same book refetched — keep the desk's typed input, but track the recomputed preview:
    if (!amountTouchedRef.current) setAmountStr(netDueDefault); // untouched default follows net_due
    if (skippedKey !== lastSkippedKeyRef.current) {
      lastSkippedKeyRef.current = skippedKey;
      setAckSkipped(false); // excluded set changed → require a fresh acknowledgement
    }
  }, [show, preview]);

  const payCcy = paymentCcy || settleCcy;
  const isCrossPay = payCcy.toUpperCase() !== settleCcy.toUpperCase();
  // LR-493 (review CU1): the editable COB (T-1) payment rate machine — default = COB settleCcy→payCcy,
  // overridable, re-seeds on pay-ccy change, never wipes a typed value, fills in when COB lands after
  // open. Shared verbatim with the Run-MTM "Run in" rate (MtmPage) via useCobEditableRate.
  const {
    rateStr: payRateOverride,
    onRateChange: onPayRateChange,
    cobRate: payCobRate,
    cobRateStr: payCobRateStr,
    effectiveRate: payRate,
    rateToSend: payFxToSend,
  } = useCobEditableRate(settleCcy, payCcy, isCrossPay);
  // The equivalent in the chosen payment currency of an amount denominated in settleCcy, at the
  // (possibly-edited) payment rate. null when not a cross-currency pay or no valid rate.
  const payEquivOf = useCallback(
    (amount: number): string | null =>
      isCrossPay && payRate != null
        ? `≈ ${formatCurrencyExact(amount * payRate, payCcy)} @ FX ${Number(payRate.toFixed(6))}`
        : null,
    [isCrossPay, payCcy, payRate]
  );

  const parsed = Number(amountStr);
  const validAmount =
    amountStr.trim() !== '' && Number.isFinite(parsed) && parsed >= 0;
  const magnitude = validAmount ? Math.round(parsed * 100) / 100 : NaN; // store cents
  const signedPosted = validAmount ? dir * magnitude : NaN;
  const poolAfter = cashpoolBefore + (validAmount ? signedPosted : 0);
  const residual = netDue - (validAmount ? signedPosted : 0);
  const hasResidual = validAmount && Math.abs(residual) >= 0.005;
  // Overcall: posted magnitude exceeds the computed call, so the "residual" is an overpayment that
  // reverses on the next call rather than an unsettled undercollection (#4).
  const isOvercall =
    validAmount && Math.abs(signedPosted) > Math.abs(netDue) + 0.005;
  const isZeroPost = validAmount && magnitude < 0.005;

  // LR-479 review #1: a COB outage routes positions to skipped[], so the exposure (and thus the
  // computed call) is understated or even reversed. Block entirely when NOTHING could be valued
  // (the call is meaningless); for a partial skip require an explicit acknowledgement.
  const skippedCount = preview?.skipped.length ?? 0;
  const hasSkipped = skippedCount > 0;
  // A New Price values a line with no COB price, never one with no COB rate into the call currency.
  const fxSkippedCount = preview?.skipped.filter((s) => s.no_fx_rate).length ?? 0;
  const skipCause =
    fxSkippedCount === 0
      ? 'no COB price'
      : fxSkippedCount === skippedCount
        ? `no COB FX rate into ${settleCcy}`
        : `no COB price, or no COB FX rate into ${settleCcy}`;
  const valuedCount = preview?.trades.length ?? 0;
  const noneValued = hasSkipped && valuedCount === 0;
  // LR-548: a subset (explicit position_ids) with any skipped line is refused by the backend (400),
  // so a skip is unrecoverable here — the trader must deselect the row or give it a New Price. This
  // hard-blocks the post (no "post anyway" acknowledgement, unlike the tolerant whole-book path).
  const subsetSkipBlocks = isSubsetCall && hasSkipped;

  // The collateral sits in a pool of another currency than the GMRA agreement's and
  // couldn't be netted. The figures shown are the un-netted exposure; the backend refuses the POST
  // outright, so block it here too and warn rather than let the desk submit into a 400.
  const fxMismatch = preview?.fx_currency_mismatch ?? false;
  // LR-548 r3 review #1: the pool predates the per-position split and its history can't be reconstructed
  // as a partition, so a selected-securities call has no honest netting basis. The backend refuses such a
  // post — block it here with the one-step way out instead of letting the trader hit a 400.
  const attributionUnavailable = preview?.pool_attribution_unavailable ?? false;
  // r4 review #1: on a SUBSET the missing attribution blocks the post; on a WHOLE-BOOK call this post IS
  // the re-baseline the subset was told to run, so it must stay available — including when the book is
  // fully collateralised (net_due 0), which is exactly where the old "nothing to post" refusal left the
  // desk with two mutually exclusive instructions and no way to create the attribution at all.
  const subsetAttributionBlocked = attributionUnavailable && isSubsetCall;
  const isRebaselinePost = attributionUnavailable && !isSubsetCall && isSettled;

  // LR-548: subset selection. `totalCount` is the whole eligible repo BOOK for this counterparty, not
  // the currently visible rows — a sidebar filter can hide eligible positions, and a call that excludes
  // them is still a strict subset of the book.
  // Review #2: the acknowledgement is driven by `isSubsetCall` (the payload really carries explicit
  // position_ids), NOT by "selected < visible". Deriving it from the visible counts let a filter-induced
  // subset — every VISIBLE row selected, book rows hidden — post with no ack while the header read
  // "N of N", i.e. a partial money-call that looked complete.
  const hasSelectionCounts = totalCount != null && selectedCount != null;
  const selCount = selectedCount ?? 0;
  const totCount = totalCount ?? 0;
  const visCount = visibleCount ?? totCount;
  const deselectedCount = Math.max(0, totCount - selCount);
  const hiddenCount = Math.max(0, totCount - visCount);
  const needsSubsetAck = hasSelectionCounts && isSubsetCall && totCount > 0;

  // Post is blocked when: in flight, the authoritative subset preview is still loading or errored,
  // the input is unusable, nothing to post (settled), nothing could be valued, a subset has an
  // un-valuable line, a foreign pool couldn't be netted, or excluded/deselected positions have not
  // been acknowledged.
  const postDisabled =
    isLoading ||
    previewLoading ||
    previewError ||
    !validAmount ||
    (isSettled && !isRebaselinePost) ||
    noneValued ||
    subsetSkipBlocks ||
    fxMismatch ||
    subsetAttributionBlocked ||
    (hasSkipped && !ackSkipped) ||
    (needsSubsetAck && !ackUnselected);

  const handleConfirm = () => {
    if (postDisabled) return;
    onConfirm(signedPosted.toFixed(2), paymentCcy || null, payFxToSend);
  };

  // MTA is informational here (a manually-agreed amount is never blocked). The preview gives it in
  // the call currency.
  const mta =
    preview?.minimum_transfer_amount != null ? Number(preview.minimum_transfer_amount) : null;
  const belowMta = mta != null && validAmount && magnitude > 0 && magnitude < mta;

  // Why lines are missing from the figures. Shown wherever the figures are, including a call that
  // computes to zero only because nothing in it could be valued.
  const skippedNotice = (
    <>
      {hasSkipped && subsetSkipBlocks && (
        // Subset call: an un-valuable selected line is unrecoverable (the backend
        // refuses it). Hard-block and tell the trader to fix the selection.
        <div className="mt-3 rounded border border-red-300 bg-red-50 px-3 py-2">
          <div className="flex items-start gap-2">
            <ExclamationTriangleIcon
              className="mt-0.5 h-4 w-4 shrink-0 text-red-600"
              aria-hidden
            />
            <p className="text-xs text-red-800">
              <span className="font-semibold">
                {skippedCount} selected position
                {skippedCount === 1 ? '' : 's'} could not be valued
              </span>{' '}
              ({skipCause}). Deselect{' '}
              {skippedCount === 1 ? 'it' : 'them'}
              {fxSkippedCount < skippedCount && ' or enter a New Price'} to post
              this selection.
            </p>
          </div>
        </div>
      )}

      {hasSkipped && !subsetSkipBlocks && (
        <div className="mt-3 rounded border border-amber-300 bg-amber-50 px-3 py-2">
          <div className="flex items-start gap-2">
            <ExclamationTriangleIcon
              className="mt-0.5 h-4 w-4 shrink-0 text-amber-600"
              aria-hidden
            />
            <p className="text-xs text-amber-800">
              <span className="font-semibold">
                {skippedCount} position
                {skippedCount === 1 ? '' : 's'} could not be
                valued
              </span>{' '}
              ({skipCause}) and{' '}
              {skippedCount === 1 ? 'is' : 'are'} excluded
              from the exposure above.{' '}
              {noneValued
                ? 'No position could be valued — posting is blocked until market data is available.'
                : 'The computed call may be understated or reversed — verify market data before posting.'}
            </p>
          </div>
          {!noneValued && (
            <label className="mt-2 flex items-center gap-2 text-xs font-medium text-amber-900">
              <input
                type="checkbox"
                checked={ackSkipped}
                onChange={(e) =>
                  setAckSkipped(e.target.checked)
                }
                className="h-3.5 w-3.5 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
              />
              Post anyway with {skippedCount} excluded
            </label>
          )}
        </div>
      )}
    </>
  );

  return (
    <Transition show={show} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[10010]"
        onClose={() => {
          if (!isLoading) onClose();
        }}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-lg transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                <DialogTitle
                  as="h3"
                  className="flex items-center gap-2 text-lg font-medium leading-6 text-gray-900"
                >
                  <span>Post repo margin call — {preview?.counterparty_name}</span>
                  {/* LR-579: which methodology the figures below were computed under. */}
                  {preview?.methodology && (
                    <span
                      className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600 whitespace-nowrap"
                      title={
                        preview.methodology === 'with_accrued'
                          ? 'Method B (price plus accrued) — unpaid accrued repo interest is included in the exposure. Set on the counterparty.'
                          : 'Method A — the price move only; accrued repo interest is excluded. Set on the counterparty.'
                      }
                    >
                      {preview.methodology === 'with_accrued' ? 'Method B' : 'Method A'}
                    </span>
                  )}
                </DialogTitle>

                {/* LR-548: scope of the call. "N of M securities selected" — amber when a strict
                    subset — plus the authoritative-preview loading hint. */}
                {hasSelectionCounts && (
                  <p className="mt-1 text-xs text-slate-500">
                    <span
                      className={clsx('font-semibold', needsSubsetAck && 'text-amber-700')}
                    >
                      {selectedCount} of {totalCount}
                    </span>{' '}
                    {totalCount === 1 ? 'security' : 'securities'} selected
                    {/* The book count can exceed what the grid shows — say so, otherwise the trader
                        hunts the filtered-out rows in the table. */}
                    {hiddenCount > 0 && ` (${hiddenCount} hidden by the current filter)`}
                    {previewLoading && ' — recalculating…'}
                  </p>
                )}

                {preview && (
                  <div className="mt-3">
                    {/* Computed summary (read-only), denominated in the settlement currency. */}
                    <div className="space-y-1 rounded border border-slate-200 bg-slate-50 px-4 py-3">
                      {/* LR-579: Method B values each repo at its Repurchase Price, so the exposure
                          is the price move PLUS the repo interest accrued and not yet paid. Show the
                          two parts and their sum, otherwise the total can't be reconciled against
                          the grid's price deltas. Method A keeps the single clean row. */}
                      {showAccruedBreakdown ? (
                        <>
                          <SummaryRow label="Clean exposure (COB − contract)">
                            {formatCurrencyExact(cleanExposure, settleCcy)}
                          </SummaryRow>
                          <SummaryRow
                            label={`Accrued interest (unpaid${preview.accrued_as_of ? `, to ${preview.accrued_as_of}` : ''})`}
                          >
                            {formatCurrencyExact(accruedComponent, settleCcy)}
                          </SummaryRow>
                          <SummaryRow label="Current Exposure (price plus accrued)">
                            {formatCurrencyExact(totalExposure, settleCcy)}
                          </SummaryRow>
                        </>
                      ) : (
                        <SummaryRow label="Current Exposure (COB − contract)">
                          {formatCurrencyExact(totalExposure, settleCcy)}
                        </SummaryRow>
                      )}
                      {/* LR-542 (feedback #63): both pool rows are displayed billing-signed
                          (flipped vs the stored ledger sign) — collecting a due-to-us call moves
                          the shown pool DOWN. The posting math itself (poolAfter/residual) stays
                          in the stored convention; only the render flips. withPoolSign adds the
                          explicit "+" on positives (formatCurrencyExact only emits "-"). */}
                      {/* LR-548: on a strict subset these pool rows are the SELECTED positions' share
                          of the pool (selected_cashpool_before), not the whole running pool — label
                          them "· selected" so the figure isn't misread as the book-wide pool, which
                          lives in the bar / cash-pool history. */}
                      <SummaryRow
                        label={
                          isSubsetCall
                            ? 'Cash pool · selected (before)'
                            : 'Cash pool (settled, before)'
                        }
                      >
                        {withPoolSign(
                          displayCashPool(cashpoolBefore),
                          formatCurrencyExact(displayCashPool(cashpoolBefore), settleCcy)
                        )}
                      </SummaryRow>
                      {/* Feedback #62: the COMPUTED figure is signed (Intl supplies the "-"), so a
                          Due to CP call reads "-$1,000,000.00" rather than a red magnitude. The
                          override INPUT below stays magnitude-only (LR-479 locked #1) — its
                          direction is fixed by this computed sign. */}
                      <SummaryRow
                        label={`Computed margin call (${noneValued ? 'nothing valued' : directionLabel(preview.direction)})`}
                        sub={payEquivOf(netDue)}
                        emphasize
                      >
                        <span className={netClass(netDue)}>
                          {formatCurrencyExact(netDue, settleCcy)}
                        </span>
                      </SummaryRow>
                    </div>

                    {convertedCcys.length > 0 && (
                      <p className="mt-1.5 text-[11px] text-slate-400">
                        Trades in {convertedCcys.join(', ')} converted to {settleCcy} at their own
                        COB (T-1) rate.
                      </p>
                    )}

                    {/* The figures above are the un-netted current exposure; posting is blocked (the
                        backend refuses it too). */}
                    {fxMismatch && (
                      <div className="mt-2 flex items-start gap-2 rounded border border-amber-300 bg-amber-50 px-3 py-2">
                        <ExclamationTriangleIcon
                          className="mt-0.5 h-4 w-4 shrink-0 text-amber-600"
                          aria-hidden
                        />
                        <p className="text-xs text-amber-800">
                          The collateral sits in a cash pool of another currency than the GMRA agreement,
                          so it could not be netted. The figures above are the current exposure only (not
                          netted against the pool). Posting is disabled; if the collateral is all in one
                          currency, change the GMRA agreement to it in the counterparty registry.
                        </p>
                      </div>
                    )}

                    {/* LR-548 r3 review #1: this counterparty's pool has no per-position attribution
                        yet, so the selected securities' share of it is unknowable — the figures above
                        are the un-netted exposure. Name the single action that fixes it rather than
                        letting the desk discover the refusal by pressing Post. */}
                    {attributionUnavailable && (
                      <div className="mt-2 flex items-start gap-2 rounded border border-amber-300 bg-amber-50 px-3 py-2">
                        <ExclamationTriangleIcon
                          className="mt-0.5 h-4 w-4 shrink-0 text-amber-600"
                          aria-hidden
                        />
                        <p className="text-xs text-amber-800">
                          {isSubsetCall ? (
                            <>
                              This cash pool predates the per-security split, so the selected
                              securities’ share of it can’t be determined — the figures above are the
                              current exposure only. Post a{' '}
                              <span className="font-semibold">whole-book</span> margin call once to
                              re-baseline it (an amount of 0 is allowed and moves no cash), then call a
                              selection.
                            </>
                          ) : (
                            <>
                              This cash pool predates the per-security split.{' '}
                              <span className="font-semibold">
                                Posting this whole-book call re-baselines it
                              </span>{' '}
                              — after that, calls on a selection of securities net against their own
                              share of the pool.
                            </>
                          )}
                        </p>
                      </div>
                    )}

                    {/* LR-493: pay the call in a different major than the settlement currency.
                        Hidden on a settled call — there is nothing to transfer, so the selector
                        would be inert (Post is disabled below). */}
                    {!isSettled && (
                      <>
                        <div className="mt-2 flex items-center justify-end gap-1.5 text-xs">
                          <label className="text-slate-500" htmlFor="repo-mc-pay-ccy">
                            Pay in
                          </label>
                          <select
                            id="repo-mc-pay-ccy"
                            value={paymentCcy}
                            onChange={(e) => setPaymentCcy(e.target.value)}
                            className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-xs text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500"
                          >
                            <option value="">{settleCcy} (agreement currency)</option>
                            {COB_FX_MAJORS.filter(
                              (c) => c.toUpperCase() !== settleCcy.toUpperCase(),
                            ).map((c) => (
                              <option key={c} value={c}>
                                {c}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* LR-493: editable payment FX rate — defaults to COB (T-1), but the desk can
                            override it or type one when COB FX is unavailable for the pair. */}
                        {isCrossPay && (
                          <div className="mt-1.5 flex items-center justify-end gap-1.5 text-xs">
                            <label className="text-slate-500" htmlFor="repo-mc-pay-fx">
                              FX {settleCcy}→{payCcy}
                            </label>
                            <input
                              id="repo-mc-pay-fx"
                              type="text"
                              inputMode="decimal"
                              value={payRateOverride}
                              onChange={(e) => onPayRateChange(e.target.value)}
                              placeholder={payCobRateStr || 'rate'}
                              className="w-28 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-right font-mono text-xs tabular-nums text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500"
                            />
                          </div>
                        )}
                      </>
                    )}

                    {/* r4 review #3: with the subset blocked there is nothing to enter — the amount block
                        would prefill a figure no outcome can produce, so drop it like a settled call does
                        and let the banner above carry the explanation. */}
                    {subsetAttributionBlocked ? null : isRebaselinePost ? (
                      <p className="mt-3 text-xs text-slate-500">
                        The computed margin call is zero, so this posts{' '}
                        <span className="font-semibold">no cash</span> — it only re-baselines this
                        pool’s per-security attribution so selections can be called next.
                      </p>
                    ) : isSettled && noneValued ? (
                      skippedNotice
                    ) : isSettled ? (
                      <p className="mt-3 text-xs text-slate-500">
                        The computed margin call is zero — the cash pool is
                        settled. There is nothing to post.
                      </p>
                    ) : (
                      <>
                        {/* Editable amount (magnitude); direction is fixed by the computed sign. */}
                        <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
                          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-blue-700">
                            Amount to post (agreed with CP)
                          </p>
                          <div className="flex items-center gap-2">
                            <span
                              className={clsx(
                                'inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-medium',
                                dir > 0
                                  ? 'bg-green-50 text-green-700'
                                  : 'bg-red-50 text-red-700'
                              )}
                            >
                              {directionLabel(preview.direction)}
                            </span>
                            <input
                              type="text"
                              inputMode="decimal"
                              aria-label="Amount to post"
                              value={amountStr}
                              onChange={(e) => {
                                amountTouchedRef.current = true;
                                setAmountStr(e.target.value);
                              }}
                              className="w-40 rounded-md border border-blue-300 px-3 py-1.5 font-mono text-base font-semibold tabular-nums focus:border-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                            />
                            <span className="text-xs text-slate-400">
                              {settleCcy}
                            </span>
                          </div>
                          {!validAmount && amountStr.trim() !== '' && (
                            <p className="mt-1.5 text-xs text-red-600">
                              Enter a valid amount (≥ 0).
                            </p>
                          )}
                          {/* LR-493: the transfer amount in the chosen payment currency at COB. */}
                          {validAmount && payEquivOf(magnitude) && (
                            <p className="mt-1.5 font-mono text-[11px] tabular-nums text-blue-700">
                              Pay {payEquivOf(magnitude)}
                            </p>
                          )}
                          {/* LR-493: no valid payment rate (COB unavailable and none entered) — the
                              backend degrades to paying in the settlement currency, so invite a rate
                              rather than imply the cross-currency transfer will happen. */}
                          {validAmount && isCrossPay && payRate == null && (
                            <p className="mt-1.5 text-[11px] text-amber-700">
                              {payCobRate == null
                                ? `No COB (T-1) rate for ${settleCcy}→${payCcy} — enter a rate to pay in ${payCcy}, or it will be paid in ${settleCcy}.`
                                : `Enter a valid FX rate to pay in ${payCcy}.`}
                            </p>
                          )}
                        </div>

                        {/* Live recompute. */}
                        <div className="mt-3 space-y-1">
                          <SummaryRow
                            label={isSubsetCall ? 'Cash pool · selected (after)' : 'Cash pool after'}
                          >
                            {validAmount
                              ? withPoolSign(
                                  displayCashPool(poolAfter),
                                  formatCurrencyExact(displayCashPool(poolAfter), settleCcy)
                                )
                              : '—'}
                          </SummaryRow>
                          <div className="flex items-center justify-between">
                            <span
                              className={clsx(
                                'text-xs',
                                hasResidual
                                  ? 'text-amber-700'
                                  : 'text-slate-500'
                              )}
                            >
                              {isOvercall
                                ? 'Overpayment → reverses next call'
                                : 'Unsettled residual → next call'}
                            </span>
                            <span
                              className={clsx(
                                'font-mono text-sm font-semibold tabular-nums',
                                hasResidual
                                  ? 'text-amber-700'
                                  : 'text-slate-900'
                              )}
                            >
                              {/* Feedback #62 (r1 #8): the residual is signed like the computed
                                  figure above it — an unsigned magnitude right under a signed
                                  headline would mix conventions in the same summary block. A
                                  sub-cent residual renders as an exact 0 so Intl never emits
                                  "-$0.00". Intl supplies the "-" — no manual prefix. */}
                              {validAmount
                                ? formatCurrencyExact(
                                    hasResidual ? residual : 0,
                                    settleCcy
                                  )
                                : '—'}
                            </span>
                          </div>
                        </div>

                        {isZeroPost && (
                          <div className="mt-3 flex items-start gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2">
                            <ExclamationTriangleIcon
                              className="mt-0.5 h-4 w-4 shrink-0 text-amber-600"
                              aria-hidden
                            />
                            <p className="text-xs text-amber-800">
                              Posting <span className="font-semibold">0</span>{' '}
                              records no transfer — the full computed amount
                              carries to the next call.
                            </p>
                          </div>
                        )}

                        {belowMta && !isZeroPost && (
                          <div className="mt-3 flex items-start gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2">
                            <ExclamationTriangleIcon
                              className="mt-0.5 h-4 w-4 shrink-0 text-amber-600"
                              aria-hidden
                            />
                            <p className="text-xs text-amber-800">
                              Amount is below the minimum transfer amount (
                              {mta != null && formatMta(mta, settleCcy)}). You can
                              still post this agreed transfer.
                            </p>
                          </div>
                        )}

                        {skippedNotice}

                        {needsSubsetAck && (
                          <div className="mt-3 rounded border border-amber-300 bg-amber-50 px-3 py-2">
                            <div className="flex items-start gap-2">
                              <ExclamationTriangleIcon
                                className="mt-0.5 h-4 w-4 shrink-0 text-amber-600"
                                aria-hidden
                              />
                              <p className="text-xs text-amber-800">
                                This call covers a{' '}
                                <span className="font-semibold">
                                  subset — {selCount} of {totCount} repo position
                                  {totCount === 1 ? '' : 's'}
                                </span>
                                . The {deselectedCount} excluded position
                                {deselectedCount === 1 ? ' is' : 's are'} left out of the exposure
                                and pool netting above
                                {hiddenCount > 0 &&
                                  ` — ${hiddenCount} of them ${
                                    hiddenCount === 1 ? 'is' : 'are'
                                  } hidden by the current filter, not deselected here`}
                                .
                              </p>
                            </div>
                            <label className="mt-2 flex items-center gap-2 text-xs font-medium text-amber-900">
                              <input
                                type="checkbox"
                                checked={ackUnselected}
                                onChange={(e) => setAckUnselected(e.target.checked)}
                                className="h-3.5 w-3.5 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                              />
                              Post for the {selCount} selected position
                              {selCount === 1 ? '' : 's'} only
                            </label>
                          </div>
                        )}

                        <p className="mt-3 text-xs text-slate-400">
                          Posting records the agreed cash transfer; any residual
                          (computed − posted) rolls into the next call.
                        </p>
                      </>
                    )}

                    {error && (
                      <div className="mt-4 rounded border border-red-200 bg-red-50 p-2 text-xs text-red-600">
                        {getErrorMessage(
                          error,
                          'Repo margin call failed. Please try again.'
                        )}
                      </div>
                    )}
                  </div>
                )}

                {!preview && (
                  <p
                    className={clsx(
                      'mt-4 text-sm',
                      previewError ? 'text-red-600' : 'text-slate-500',
                    )}
                  >
                    {previewLoading
                      ? 'Calculating the margin call for the selected positions…'
                      : previewError
                        ? "Couldn't calculate the margin call for the selected positions. Close and try again."
                        : 'No margin call to preview.'}
                  </p>
                )}

                <div className="mt-4 flex justify-end gap-3">
                  <button
                    type="button"
                    disabled={isLoading}
                    className="inline-flex justify-center rounded-md border border-transparent bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2 disabled:opacity-50"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={postDisabled}
                    className="inline-flex min-w-[80px] justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-50"
                    onClick={handleConfirm}
                  >
                    {isLoading ? (
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    ) : (
                      'Post margin call'
                    )}
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
